import React from "react";
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Global sticky "Back" button.
 * - Fixed top-left, semi-transparent pill, blurred background.
 * - Hidden on root landing / auth / agent microsite root (nothing meaningful to go back to).
 * - On any /agent/:slug/* sub-route → always navigates back to /agent/:slug (microsite home)
 *   so visitors return to the agent context rather than jumping across the browser stack.
 * - Otherwise falls back to browser history (navigate(-1)); if there's no history
 *   entry, we route to "/" as a safe default.
 */
const HIDDEN_EXACT_PATHS = new Set(["/", "/login", "/register", "/subscribe", "/onboarding"]);

export default function BackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const agentMatch = useMatch("/agent/:slug/*");

  const path = location.pathname;

  // Hide on landing / auth pages
  if (HIDDEN_EXACT_PATHS.has(path)) return null;

  // Hide on agent microsite root (/agent/:slug) — that IS the home for the microsite
  if (agentMatch && (agentMatch.params["*"] === "" || agentMatch.params["*"] == null)) {
    return null;
  }

  const handleClick = () => {
    // Agent-context sub-route → route to the agent microsite root
    if (agentMatch && agentMatch.params.slug) {
      navigate(`/agent/${agentMatch.params.slug}`);
      return;
    }
    // Prefer browser history, fall back to home
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="global-back-button"
      aria-label="Go back"
      className="
        fixed z-[60]
        top-3 left-3 sm:top-4 sm:left-4
        inline-flex items-center gap-2
        px-3 py-1.5 sm:px-4 sm:py-2
        rounded-full
        bg-white/70 hover:bg-white/90
        backdrop-blur-md
        border border-black/10
        shadow-sm hover:shadow
        text-sm font-medium text-sg-navy
        transition-all duration-150
        focus:outline-none focus-visible:ring-2 focus-visible:ring-sg-gold-deep/50
      "
    >
      <ArrowLeft className="w-4 h-4" />
      <span className="hidden sm:inline">Back</span>
    </button>
  );
}
